import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getShrimpSalesKey } from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { BaseResponse } from 'common/types/api'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { SALES_REQUEST_CONFIG } from '.'
import { CreateSalesVariables, EditSalesVariables, UploadBillResponse } from './types'

export const useMutateCreateShrimpSales = () => {
  const cycleApi = useCycleApi()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(
    ({ body: data }: CreateSalesVariables) =>
      cycleApi.request({ ...SALES_REQUEST_CONFIG.create, data }),
    {
      onSuccess: () => {
        enqueueSnackbar(t('common:create.success'), { variant: 'success' })
        queryClient.invalidateQueries(getShrimpSalesKey.all())
      },
    }
  )
}

export const useMutateUploadBill = () => {
  const cycleApi = useCycleApi()
  return useMutation((data: FormData) =>
    cycleApi.request<BaseResponse<UploadBillResponse>>({
      ...SALES_REQUEST_CONFIG.uploadImage,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data,
    })
  )
}

export const useMutateDeleteShrimpSales = () => {
  const cycleApi = useCycleApi()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(
    (id: number) =>
      cycleApi.request({
        ...SALES_REQUEST_CONFIG.delete,
        url: `${SALES_REQUEST_CONFIG.delete.url}/${id}`,
      }),
    {
      onSuccess: () => {
        enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
        queryClient.invalidateQueries(getShrimpSalesKey.all())
      },
    }
  )
}

export const useMutateUpdateShrimpSales = () => {
  const cycleApi = useCycleApi()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(
    ({ salesId, body: data }: EditSalesVariables) =>
      cycleApi.request({
        ...SALES_REQUEST_CONFIG.edit,
        url: `${SALES_REQUEST_CONFIG.edit.url}/${salesId}`,
        data,
      }),
    {
      onSuccess: () => {
        enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
        queryClient.invalidateQueries(getShrimpSalesKey.all())
      },
    }
  )
}
